import React from "react";
import { Calendar as CalendarIcon, MapPin, Mic2, Repeat, Clock } from "lucide-react";
import { isWeekly, weeklyDow, TYPE_LABEL } from "@/hooks/useEvents";

const longDate = (d) => {
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
  catch { return d; }
};

const formatRange = (start, end) => {
  if (!start) return "";
  return end && end !== start ? `${longDate(start)} – ${longDate(end)}` : longDate(start);
};

const Meta = ({ ev, className }) => (
  <div className={`flex flex-col gap-1 text-xs ${className}`}>
    {ev.speaker && <div className="flex items-center gap-1.5"><Mic2 size={12} /> {ev.speaker}</div>}
    {ev.location && <div className="flex items-center gap-1.5"><MapPin size={12} /> {ev.location}</div>}
  </div>
);

const WeeklyCard = ({ ev, timeStr }) => (
  <article data-testid={`event-card-${ev.id}`} className="p-6 rounded-sm transition-all bg-[#003B5C] text-white border-2 border-[#D4A437] hover:shadow-md">
    <div data-testid={`event-recurring-badge-${ev.id}`} className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] bg-[#D4A437] text-white px-2 py-1 rounded-sm mb-3">
      <Repeat size={11} /> Weekly · Every {weeklyDow(ev)}
    </div>
    <h3 className="font-display text-xl font-bold text-white leading-tight mb-2">{ev.title}</h3>
    {timeStr && <div className="flex items-center gap-1.5 text-xs text-[#D4A437] font-semibold mb-2"><Clock size={12} /> {timeStr}</div>}
    {ev.description && <p className="text-sm text-white/80 mb-3 whitespace-pre-line">{ev.description}</p>}
    <div className="flex flex-col gap-1 text-xs text-white/70">
      {ev.date && (
        <div className="flex items-center gap-1.5">
          <CalendarIcon size={12} /> Starts {longDate(ev.date)}{ev.end_date ? ` · through ${longDate(ev.end_date)}` : ""}
        </div>
      )}
      <Meta ev={ev} className="text-white/70" />
    </div>
  </article>
);

const DatedCard = ({ ev, timeStr }) => (
  <article data-testid={`event-card-${ev.id}`} className="p-6 rounded-sm transition-all bg-white border border-gray-100 hover:shadow-md hover:border-[#D4A437]">
    <div className="flex items-center justify-between gap-2 mb-3">
      <div className="flex items-center gap-2 text-[#D4A437] text-xs font-bold uppercase tracking-[0.2em]">
        <CalendarIcon size={12} /> {formatRange(ev.date, ev.end_date)}
      </div>
      <span data-testid={`event-type-badge-${ev.id}`} className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#003B5C] bg-[#003B5C]/8 border border-[#003B5C]/15 px-2 py-0.5 rounded-sm">
        {TYPE_LABEL[ev.event_type] || "One-time"}
      </span>
    </div>
    <h3 className="font-display text-xl font-bold text-[#003B5C] leading-tight mb-2">{ev.title}</h3>
    {timeStr && <div className="flex items-center gap-1.5 text-xs text-[#003B5C] font-semibold mb-2"><Clock size={12} /> {timeStr}</div>}
    {ev.description && <p className="text-sm text-[#1E1E1E]/70 mb-3">{ev.description}</p>}
    <Meta ev={ev} className="text-[#1E1E1E]/60" />
  </article>
);

export const EventCard = ({ ev }) => {
  const timeStr = [ev.start_time, ev.end_time].filter(Boolean).join(" – ");
  return isWeekly(ev) ? <WeeklyCard ev={ev} timeStr={timeStr} /> : <DatedCard ev={ev} timeStr={timeStr} />;
};
